import { FC, useEffect, useRef, useState } from 'react'
import { declOfNum } from '../../../lib/utils'
import CardQuiz from '../CardQuiz/CardQuiz'
import Card from '../Card/Card'
import Title from '../../common/Title/Title'
import Button from '../../common/Button/Button'
import { ButtonTheme, ICatalogItem } from '../../types'
import {
  ElementOrderByInput,
  SortOrder,
} from '../../../__generated__/globalTypes'
import LoaderSvg from '../../../public/static/icons/form-loader.svg'
import PlatesIcon from '../../../public/static/icons/catalog/plates-icon.svg'
import GridIcon from '../../../public/static/icons/catalog/grid-icon.svg'
import css from './List.module.scss'
import cx from 'classnames'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
export enum GridTypes {
  grid = 'grid',
  plates = 'plates',
}

interface List {
  count: number
  name: string
  description?: string
  order?: ElementOrderByInput
  setOrder?: React.Dispatch<React.SetStateAction<ElementOrderByInput>>
  elements: ICatalogItem[]
  loading?: boolean
  sectionCode?: string
  setIsQuizOpen?: React.Dispatch<React.SetStateAction<boolean>>
  hasNextPage: boolean
  loadMore?: () => Promise<void>
  sortUpdate?: (sort: ElementOrderByInput) => void
  refetching?: boolean
}
const List: FC<List> = ({
  count,
  name,
  description,
  order,
  setOrder,
  elements,
  loading,
  sectionCode,
  setIsQuizOpen,
  hasNextPage,
  loadMore,
  sortUpdate,
  refetching,
}) => {
  const [type, setType] = useState<GridTypes>(GridTypes.plates)
  const [gridType, setGridType] = useLocalStorage(
    'km-catalog-grid',
    GridTypes.grid,
  )
  const loadMoreButton = useRef<HTMLButtonElement>(null)
  const observer = useRef<IntersectionObserver>(null)
  const loadMoreRef = useRef<() => Promise<void>>(loadMore)
  loadMoreRef.current = loadMore

  function callback(entries: IntersectionObserverEntry[]): void {
    if (loading) return

    if (entries[0].isIntersecting) {
      loadMoreRef.current()
    }
  }

  useEffect(() => {
    setType(gridType)
  }, [gridType])

  useEffect(() => {
    if (!loadMoreButton.current || !hasNextPage) return
    if (observer.current) observer.current.disconnect()

    observer.current = new IntersectionObserver(callback)
    observer.current.observe(loadMoreButton.current)

    return () => observer.current?.disconnect()
  }, [loadMoreButton, hasNextPage])

  useEffect(() => {
    if (!hasNextPage) {
      observer.current?.disconnect()
    }
  }, [hasNextPage])

  return (
    <>
      <div className={css.title}>
        <Title className={name.includes('детей') ? css.kids : ''}>{name}</Title>
      </div>
      {description && <p className={css.description}>{description}</p>}
      <div className={css.header}>
        {loading || count === undefined || (
          <p className={css.count}>
            {count} {declOfNum(count, ['товар', 'товара', 'товаров'])}
          </p>
        )}
        {order && (
          <div className={css.sortingWrapper}>
            <label className={css.sorting}>сортировка </label>
            <select
              className={css.select}
              value={order.sort ? 'default' : 'new'}
              onChange={(e) => {
                switch (e.target.value) {
                  case 'new': {
                    setOrder({ createdAt: SortOrder.desc })
                    sortUpdate({ createdAt: SortOrder.desc })
                    return
                  }
                  case 'default': {
                    setOrder({ sort: SortOrder.asc })
                    sortUpdate({ sort: SortOrder.asc })
                    return
                  }
                }
              }}
            >
              <option value="default">по умолчанию</option>
              <option value="new">по новизне</option>
            </select>
          </div>
        )}

        <div className={css.buttons}>
          <button
            className={cx(type === GridTypes.plates && css.active)}
            type="button"
            onClick={() => setGridType(GridTypes.plates)}
          >
            <PlatesIcon />
          </button>
          <button
            className={cx(type === GridTypes.grid && css.active)}
            type="button"
            onClick={() => setGridType(GridTypes.grid)}
          >
            <GridIcon />
          </button>
        </div>
      </div>

      {elements?.length > 0 && !refetching && (
        <ul
          className={cx(
            css.list,
            type === GridTypes.plates ? css.plates : css.grid,
          )}
        >
          {sectionCode === 'mineralnaya_kosmetika_dlya_litsa' && (
            <CardQuiz
              openHandler={setIsQuizOpen}
              className={css.quizCard}
              isSmall={!(type === GridTypes.plates)}
            />
          )}

          {elements?.map((element, index) => (
            <Card
              footerClassName={css.cardFooter}
              wrapperClass={css.cardWrapper}
              key={element.id}
              element={element}
              index={index}
              kind={type}
              category={sectionCode}
              isGrid={type === GridTypes.grid}
            />
          ))}
        </ul>
      )}

      {refetching && (
        <div className={css.loader}>
          <LoaderSvg />
        </div>
      )}
      {!elements?.length && (
        <>
          {!loading && !refetching && (
            <div className={css.empty}>В разделе нет товаров</div>
          )}

          {sectionCode === 'mineralnaya_kosmetika_dlya_litsa' && !refetching && (
            <div className={css.list}>
              <CardQuiz openHandler={setIsQuizOpen} />
            </div>
          )}
        </>
      )}
      {hasNextPage && (
        <>
          {loading && (
            <div className={css.loader}>
              <LoaderSvg />
            </div>
          )}
          <div className={cx(css.footer, loading && css['footer--hidden'])}>
            <Button
              theme={ButtonTheme.ellipseBig}
              onClick={() => loading || loadMore()}
              ref={loadMoreButton}
            >
              Еще товары
            </Button>
          </div>
        </>
      )}
    </>
  )
}

export default List
